import React, { useState, useEffect } from "react";
import axios from "axios";
import Button from "../common/Button";
import toast from "react-hot-toast";
import { toastOptions } from "../common/toastOptions";

type DeleteOnExportMode = "trash" | "permanent";

const DataRetentionConfiguration: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Local form state
  const [deleteOnExport, setDeleteOnExport] = useState(false);
  const [mode, setMode] = useState<DeleteOnExportMode>("trash");
  const [retentionEnabled, setRetentionEnabled] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get("/api/agency/data-retention-settings");
      setDeleteOnExport(data.delete_on_export || false);
      setMode(
        data.delete_on_export_mode === "permanent" ? "permanent" : "trash",
      );
      setRetentionEnabled(data.trash_retention_enabled || false);
      setRetentionDays(data.trash_retention_days ?? 30);
    } catch (err) {
      console.error("Error fetching data retention settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (
      retentionEnabled &&
      (!Number.isInteger(retentionDays) ||
        retentionDays < 1 ||
        retentionDays > 365)
    ) {
      toast.error(
        "Trash retention must be between 1 and 365 days.",
        toastOptions,
      );
      return;
    }

    try {
      setSaving(true);
      await axios.patch("/api/agency/data-retention-settings", {
        delete_on_export: deleteOnExport,
        delete_on_export_mode: mode,
        trash_retention_enabled: retentionEnabled,
        trash_retention_days: retentionDays,
      });
      toast.success(
        "Data retention settings saved successfully.",
        toastOptions,
      );
      fetchSettings();
    } catch (err) {
      toast.error("Failed to save data retention settings.", toastOptions);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 card border border-base-300 bg-base-100 mt-8">
        <p className="text-base-content/60">
          Loading data retention settings...
        </p>
      </div>
    );

  return (
    <div className="p-6 card border border-base-300 bg-base-100 mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-base-300">
        <div>
          <h2 className="text-2xl font-semibold text-base-content mb-2">
            Data Retention
          </h2>
          <p className="text-base-content/60">
            Control what happens to cases after officers export their reports,
            and how long trashed cases are kept before permanent deletion.
          </p>
        </div>
      </div>

      <div>
        {/* Delete on export toggle */}
        <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg border border-base-200">
          <div>
            <h3 className="font-medium text-base-content">
              Delete Cases Upon Export
            </h3>
            <p className="text-sm text-base-content/60 mt-1">
              When enabled, officers see a pre-checked "Delete case upon export"
              option in the export dialogs. They can uncheck it to keep a
              specific case.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
            <input
              type="checkbox"
              checked={deleteOnExport}
              onChange={(e) => setDeleteOnExport(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Deletion mode — collapses open/closed with the toggle above */}
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            deleteOnExport
              ? "grid-rows-[1fr] opacity-100 visible"
              : "grid-rows-[0fr] opacity-0 invisible"
          }`}
        >
          <div className="overflow-hidden">
            <div className="mt-6 space-y-3 p-6 bg-base-200 rounded-lg border border-base-200">
              <h3 className="text-gray-800 dark:text-base-content/80 font-medium text-sm mb-3">
                Deletion Mode
              </h3>

              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  mode === "trash"
                    ? "bg-base-100 border-blue-500 ring-1 ring-blue-500"
                    : "bg-base-100 border-base-300"
                }`}
              >
                <input
                  type="radio"
                  name="delete-on-export-mode"
                  checked={mode === "trash"}
                  onChange={() => setMode("trash")}
                  className="mt-0.5 accent-blue-600"
                />
                <div>
                  <span className="text-sm font-medium text-base-content">
                    Move to Trash Bin
                  </span>
                  <p className="text-xs text-base-content/60 mt-0.5">
                    Exported cases go to the trash bin, where they can be
                    restored
                    {retentionEnabled
                      ? " until the trash retention period below elapses"
                      : ""}
                    .
                  </p>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  mode === "permanent"
                    ? "bg-red-50 dark:bg-red-950/40 border-red-500 ring-1 ring-red-500"
                    : "bg-base-100 border-base-300"
                }`}
              >
                <input
                  type="radio"
                  name="delete-on-export-mode"
                  checked={mode === "permanent"}
                  onChange={() => setMode("permanent")}
                  className="mt-0.5 accent-red-600"
                />
                <div>
                  <span className="text-sm font-medium text-base-content">
                    Permanently Delete
                  </span>
                  <p className="text-xs text-base-content/60 mt-0.5">
                    The case skips the trash bin entirely. The report, audio
                    recordings, transcripts, and all attached files are
                    destroyed immediately and cannot be recovered.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Trash retention toggle */}
        <div className="mt-6 flex items-center justify-between p-4 bg-base-200 rounded-lg border border-base-200">
          <div>
            <h3 className="font-medium text-base-content">
              Automatically Delete Trashed Cases
            </h3>
            <p className="text-sm text-base-content/60 mt-1">
              When enabled, cases in the trash bin older than the period below
              are permanently deleted, including all audio and transcripts. When
              disabled, trashed cases are hidden from the trash bin after 30
              days but never deleted.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
            <input
              type="checkbox"
              checked={retentionEnabled}
              onChange={(e) => setRetentionEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Retention period — collapses open/closed with the toggle above */}
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            retentionEnabled
              ? "grid-rows-[1fr] opacity-100 visible"
              : "grid-rows-[0fr] opacity-0 invisible"
          }`}
        >
          <div className="overflow-hidden">
            <div className="mt-6 p-6 bg-base-200 rounded-lg border border-base-200">
              <h3 className="text-gray-800 dark:text-base-content/80 font-medium text-sm mb-1">
                Trash Retention Period
              </h3>
              <p className="text-xs text-base-content/60 mb-3">
                Applies to every trashed case, however it got there.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={retentionDays}
                  onChange={(e) =>
                    setRetentionDays(parseInt(e.target.value) || 0)
                  }
                  className="w-20 px-3 py-2 border border-gray-300 dark:border-base-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-base-content/60">days</span>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-3">
                Enabling this, or lowering the period, permanently deletes any
                trashed cases already older than the period within the hour.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-2">
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataRetentionConfiguration;
